import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { Users, BookOpen, BarChart3, LogOut, Settings, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { SMTPConfig } from "./SMTPConfig";
import { UserManagement, ModuleManagement, Analytics } from "./admin";

export const AdminDashboard = ({ user, onLogout }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('users');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const path = location.pathname.split('/')[2] || 'users';
    setActiveTab(path);
  }, [location]);

  return (
    <div className="min-h-screen bg-cream-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-sage-200">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                className="md:hidden p-2 rounded-lg hover:bg-sage-50"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Settings className="w-6 h-6 text-sage-600" />
              </button>
              <div className="flex items-center space-x-3">
                <img 
                  src="/useba-logo.png" 
                  alt="uSeba Logo" 
                  className="h-8 sm:h-10"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <h1 className="text-lg sm:text-xl font-semibold text-charcoal-800 font-display">
                    uSeba Admin
                  </h1>
                  <p className="text-xs sm:text-sm text-charcoal-600 hidden sm:block">{user?.email}</p>
                </div>
              </div>
            </div>
            <Button 
              onClick={onLogout}
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar - Mobile */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-sage-200 p-4">
            <nav className="space-y-2">
              <Link
                to="/admin/users"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'users' ? 'bg-sage-100 text-sage-800' : 'text-charcoal-600 hover:bg-sage-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Users className="w-5 h-5" />
                <span>Users</span>
              </Link>
              <Link
                to="/admin/modules"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'modules' ? 'bg-sage-100 text-sage-800' : 'text-charcoal-600 hover:bg-sage-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen className="w-5 h-5" />
                <span>Content</span>
              </Link>
              <Link
                to="/admin/analytics"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'analytics' ? 'bg-sage-100 text-sage-800' : 'text-charcoal-600 hover:bg-sage-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <BarChart3 className="w-5 h-5" />
                <span>Analytics</span>
              </Link>
              <Link
                to="/admin/smtp"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'smtp' ? 'bg-sage-100 text-sage-800' : 'text-charcoal-600 hover:bg-sage-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Mail className="w-5 h-5" />
                <span>SMTP</span>
              </Link>
            </nav>
          </div>
        )}

        {/* Sidebar - Desktop */}
        <aside className="hidden md:block w-64 bg-white border-r border-sage-200 min-h-[calc(100vh-73px)]">
          <nav className="p-4 space-y-2">
            <Link
              to="/admin/users"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'users' ? 'bg-sage-100 text-sage-800' : 'text-charcoal-600 hover:bg-sage-50'
              }`}
            >
              <Users className="w-5 h-5" />
              <span>User Management</span>
            </Link>
            <Link
              to="/admin/modules"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'modules' ? 'bg-sage-100 text-sage-800' : 'text-charcoal-600 hover:bg-sage-50'
              }`}
            >
              <BookOpen className="w-5 h-5" />
              <span>Content Management</span>
            </Link>
            <Link
              to="/admin/analytics"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'analytics' ? 'bg-sage-100 text-sage-800' : 'text-charcoal-600 hover:bg-sage-50'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
              <span>Analytics</span>
            </Link>
            <Link
              to="/admin/smtp"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'smtp' ? 'bg-sage-100 text-sage-800' : 'text-charcoal-600 hover:bg-sage-50'
              }`}
            >
              <Mail className="w-5 h-5" />
              <span>SMTP Settings</span>
            </Link>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route path="users" element={<UserManagement />} />
            <Route path="modules" element={<ModuleManagement />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="smtp" element={<SMTPConfig />} />
            <Route path="*" element={<UserManagement />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};
